import React, { useRef, useState } from "react";
import "./App.css";
import * as BABYLON from "@babylonjs/core/Legacy/legacy";
import { MixMaterial, WaterMaterial } from "@babylonjs/materials";
import { useGameEngine, useDrawingOnTerrain } from "./hooks";
import {
  generateMixMap,
  generateNoiseV2,
  GeneratorProperties,
  MapOptions,
  MapProperties,
  MapType,
  openAIgenerateImage,
  TextureType,
  updateTerrain,
  updateTerrainTexture,
} from "./generators";
import { DrawOptions, Options } from "./components";

export const RESOLUTION = {
  x: 256,
  y: 256,
};

export const SUBDIVISIONS = 200;

const DEFAULT_MAP_SIZE = 100;

const HISTORY_LENGTH = 1000;

function App() {
  const noiseCanvasRef = useRef<HTMLCanvasElement>(null);
  const mixMapCanvasRef = useRef<HTMLCanvasElement>(null);

  const babylonCanvasRef = useRef<HTMLCanvasElement>(null);

  const { gameScene } = useGameEngine(babylonCanvasRef);

  const [mapType, setMapType] = useState<MapType>("GREEN_HILLS");

  const [ground, setGround] = useState<BABYLON.GroundMesh>();
  const [water, setWater] = useState<BABYLON.Mesh>();

  const [history, setHistory] = React.useState<ImageData[]>([]);

  const [defaultMapOptions, setDefaultMapOptions] = React.useState<MapOptions>(
    MapProperties[mapType]
  );

  const [mapSize, setMapSize] = React.useState(DEFAULT_MAP_SIZE);

  const [mapOptions, setMapOptions] =
    React.useState<MapOptions>(defaultMapOptions);

  React.useEffect(() => {
    setDefaultMapOptions(MapProperties[mapType]);
    setMapOptions(MapProperties[mapType]);
  }, [mapType]);

  const getNoiseContextAndData = React.useCallback(() => {
    if (!noiseCanvasRef.current) {
      return null;
    }
    const noiseCanvas = noiseCanvasRef.current;

    const width = noiseCanvas.width;
    const height = noiseCanvas.height;
    const noiseCtx = noiseCanvasRef.current.getContext("2d", {
      willReadFrequently: true,
    });

    if (!noiseCtx) {
      return null;
    }

    const noiseData = noiseCtx?.getImageData(0, 0, width, height);

    setHistory((current) => [...current.slice(-HISTORY_LENGTH), noiseData]);

    return { noiseCtx, noiseData, width, height, noiseCanvas };
  }, [noiseCanvasRef]);

  const redrawTerrain = React.useCallback(() => {
    if (!ground || !noiseCanvasRef.current || !mixMapCanvasRef.current) {
      return;
    }

    updateTerrain(
      ground,
      noiseCanvasRef.current,
      RESOLUTION,
      mapOptions.heightScale,
      mapSize,
      SUBDIVISIONS
    );
    updateTerrainTexture(
      gameScene,
      RESOLUTION,
      mixMapCanvasRef.current,
      mapOptions.textures
    );
  }, [
    gameScene,
    ground,
    noiseCanvasRef,
    mixMapCanvasRef,
    mapOptions.textures,
    mapOptions.heightScale,
    mapSize,
  ]);

  const {
    setDrawing,
    moveBrush,
    setBrushHidden,
    brushHidden,
    brushSize,
    noiseLevel,
    setBrushSize,
    setNoiseLevel,
    brushRef,
    fillMode,
    setFillmode,
    setSlope,
    slope,
  } = useDrawingOnTerrain({
    getNoise: getNoiseContextAndData,
    gameScene,
    noiseCanvasRef,
    mixMapCanvasRef,
    ground,
    mapType,
    redrawTerrain,
    mapOptions,
    mapSize,
  });

  const handleKeyShortcuts = React.useCallback(
    (event) => {
      // event.preventDefault();
      let charCode = String.fromCharCode(event.which).toLowerCase();
      if ((event.ctrlKey || event.metaKey) && charCode === "z") {
        const prevImageData = history[history.length - 1];
        if (!prevImageData) {
          return;
        }

        const noiseCtx = noiseCanvasRef.current?.getContext("2d", {
          willReadFrequently: true,
        });
        if (!noiseCtx) {
          return;
        }

        noiseCtx.putImageData(prevImageData, 0, 0);
        generateMixMap(
          mixMapCanvasRef.current,
          noiseCanvasRef.current,
          mapOptions.levels
        );
        redrawTerrain();

        setHistory((history) => history.slice(0, history.length - 1));
      }
    },
    [history, redrawTerrain, mixMapCanvasRef, noiseCanvasRef, mapOptions.levels]
  );

  React.useEffect(() => {
    window.addEventListener("keydown", handleKeyShortcuts);

    return () => {
      window.removeEventListener("keydown", handleKeyShortcuts);
    };
  }, [handleKeyShortcuts]);

  const generateTerrain = React.useCallback(() => {
    console.log("GENERATE");
    if (ground) {
      console.error(
        "Terrain is already generated. Use 'updateTerrain' instead"
      );
      return;
    }

    if (noiseCanvasRef.current && mixMapCanvasRef.current && gameScene) {
      const heightUrl = noiseCanvasRef.current.toDataURL("image/png");
      const mixMapUrl = mixMapCanvasRef.current.toDataURL("image/png");

      const { textures, heightScale } = MapProperties[mapType];

      const newGround = BABYLON.MeshBuilder.CreateGroundFromHeightMap(
        "gdhm",
        heightUrl,
        {
          width: mapSize,
          height: mapSize,
          subdivisions: SUBDIVISIONS,
          maxHeight: heightScale / 2,
          minHeight: -heightScale / 2,
        },
        gameScene
      );

      newGround.enablePointerMoveEvents = true;
      console.log("GENERATE TERRAIN FOR ", mapType, heightScale, textures);

      const terrainMaterial =
        (gameScene.getMaterialByName("terrainMaterial") as MixMaterial) ||
        new MixMaterial("terrainMaterial", gameScene);
      terrainMaterial.specularColor = new BABYLON.Color3(0.5, 0.5, 0.5);
      terrainMaterial.specularPower = 64;
      terrainMaterial.mixTexture1 = new BABYLON.Texture(mixMapUrl, gameScene);

      terrainMaterial.diffuseTexture1 = new BABYLON.Texture(
        `textures/${textures[0].name}.jpg`,
        gameScene
      );
      terrainMaterial.diffuseTexture2 = new BABYLON.Texture(
        `textures/${textures[1].name}.jpg`,
        gameScene
      );
      terrainMaterial.diffuseTexture3 = new BABYLON.Texture(
        `textures/${textures[2].name}.jpg`,
        gameScene
      );
      terrainMaterial.diffuseTexture4 = new BABYLON.Texture(
        `textures/${textures[3].name}.jpg`,
        gameScene
      );

      terrainMaterial.diffuseTexture1.uScale =
        terrainMaterial.diffuseTexture1.vScale = textures[0].uv || 50;

      terrainMaterial.diffuseTexture2.uScale =
        terrainMaterial.diffuseTexture2.vScale = textures[1].uv || 50;

      terrainMaterial.diffuseTexture3.uScale =
        terrainMaterial.diffuseTexture3.vScale = textures[2].uv || 50;

      terrainMaterial.diffuseTexture4.uScale =
        terrainMaterial.diffuseTexture4.vScale = textures[3].uv || 50;

      newGround.material = terrainMaterial;

      setGround(newGround);

      let waterMesh = BABYLON.MeshBuilder.CreateGround(
        "waterMesh",
        {
          width: mapSize,
          height: mapSize,
          subdivisions: 100,
        },
        gameScene
      );

      let water = new WaterMaterial(
        "water",
        gameScene,
        new BABYLON.Vector2(512, 512)
      );
      water.bumpTexture = new BABYLON.Texture(
        "textures/waterBump.png",
        gameScene
      );
      water.windDirection = new BABYLON.Vector2(1, 1);
      water.backFaceCulling = true;
      water.windForce = -1;
      water.waveHeight = 0.1;
      water.waveLength = 0.1;
      water.bumpHeight = 0.1;
      water.waveCount = 100;
      water.waterColor = new BABYLON.Color3(0.047, 0.23, 0.17);
      water.colorBlendFactor = 0.25;
      water.addToRenderList(newGround);
      water.addToRenderList(gameScene.getMeshByName("skyBox"));

      waterMesh.material = water;

      waterMesh.position.y = 3;

      setWater(waterMesh);
    }
  }, [noiseCanvasRef, mixMapCanvasRef, gameScene, ground, mapType, mapSize]);

  const loadImage = React.useCallback(
    (evt: any) => {
      if (!evt || !evt.target) return;
      const image = new Image(RESOLUTION.x, RESOLUTION.y);
      image.onload = () => {
        const ctx = noiseCanvasRef.current?.getContext("2d");
        ctx?.drawImage(image, 0, 0, image.width, image.height);
        generateMixMap(
          mixMapCanvasRef.current,
          noiseCanvasRef.current,
          mapOptions.levels
        );
        redrawTerrain();
      };
      image.src = URL.createObjectURL(evt.target.files[0]);
    },
    [noiseCanvasRef, redrawTerrain, mixMapCanvasRef, mapOptions.levels]
  );

  const clearNoise = React.useCallback(() => {
    const canvas = noiseCanvasRef.current;
    if (!canvas) return;
    const { width, height } = canvas;
    canvas.getContext("2d")?.clearRect(0, 0, width, height);
  }, [noiseCanvasRef]);

  React.useEffect(() => {
    if (water) {
      const {
        bumpHeight,
        colorBlendFactor,
        level,
        show,
        waterColor,
        waveHeight,
        waveLength,
        windForce,
        waveCount
      } = mapOptions.water;
      water.position.y = level;
      water.visibility = Number(show);
      const material = water.material as WaterMaterial;
      material.windForce = windForce;
      material.waveHeight = waveHeight;
      material.waveLength = waveLength;
      material.bumpHeight = bumpHeight;
      material.waterColor.set(...waterColor);
      material.colorBlendFactor = colorBlendFactor;
      material.waveCount = waveCount;
    }
  }, [mapOptions.water, water]);

  React.useEffect(() => {
    if (water) {
      water.scaling.x = mapSize / DEFAULT_MAP_SIZE;
      water.scaling.z = mapSize / DEFAULT_MAP_SIZE;
    }
  }, [mapSize, water]);

  React.useEffect(() => {
    if (!ground) {
      generateNoiseV2(
        GeneratorProperties[mapType],
        getNoiseContextAndData
      ).then(() => {
        generateMixMap(
          mixMapCanvasRef.current,
          noiseCanvasRef.current,
          mapOptions.levels
        );
        generateTerrain();
      });
    }
  }, [
    ground,
    generateTerrain,
    redrawTerrain,
    mapType,
    getNoiseContextAndData,
    mapOptions.levels,
  ]);

  return (
    <div className="App">
      <div className="left">
        <div className="inteface">
          <canvas
            className="drawing-canvas"
            width={RESOLUTION.x}
            height={RESOLUTION.y}
            ref={noiseCanvasRef}
            onMouseEnter={() => setBrushHidden(false)}
            onMouseLeave={() => {
              setBrushHidden(true);
              setDrawing(false);
            }}
            onMouseMove={(evt) => moveBrush(evt)}
            onMouseDown={(evt) => {
              if (evt.button === 0) setDrawing(true);
            }}
            onMouseUp={() => setDrawing(false)}
          ></canvas>
          <canvas
            className="mixmap-canvas"
            width={RESOLUTION.x}
            height={RESOLUTION.y}
            ref={mixMapCanvasRef}
          ></canvas>
          <DrawOptions
            brushHidden={brushHidden}
            brushRef={brushRef}
            brushSize={brushSize}
            noiseLevel={noiseLevel}
            setBrushSize={setBrushSize}
            setNoiseLevel={setNoiseLevel}
            fillMode={fillMode}
            setFillMode={setFillmode}
            setSlope={setSlope}
            slope={slope}
          />
        </div>
        <canvas ref={babylonCanvasRef} className="babylonCanvas"></canvas>
      </div>
      <div className="right">
        <Options
          getNoise={getNoiseContextAndData}
          redrawTerrain={redrawTerrain}
          loadImage={loadImage}
          mapType={mapType}
          mixMapCanvasRef={mixMapCanvasRef}
          noiseCanvasRef={noiseCanvasRef}
          setMapType={setMapType}
          clearNoise={clearNoise}
          mapOptions={mapOptions}
          setMapOptions={setMapOptions}
          setMapSize={setMapSize}
          mapSize={mapSize}
        />
      </div>
    </div>
  );
}

export default App;
